import { defineSlotRecipe } from '@chakra-ui/react';
import { Colors, TextStyles, Space, Radii, Shadows, FontWeights } from '../../types';

export const menuSlotRecipe = defineSlotRecipe({
  slots: [
    'root',
    'trigger',
    'triggerItem',
    'content',
    'item',
    'itemGroup',
    'itemGroupLabel',
    'separator',
    'itemIndicator',
    'itemText',
  ],
  base: {
    content: {
      bg: Colors.SURFACE_ELEVATED,
      borderRadius: Radii.XL,
      boxShadow: Shadows.LG,
      p: Space['2XS'],
      zIndex: 'dropdown',
      border: 'none',
      outline: '1px solid',
      outlineColor: Colors.BORDER_ELEVATED,
      overflow: 'hidden',
      overflowY: 'auto',
      maxHeight: 'var(--available-height)',
      // Enter is slide-only: Chakra's Menu recipe sets `slide-fade-in` on `_open`, and a running or frozen fade
      // keyframe beats the static opacity, so declaring `animationName` here replaces that keyframe outright.
      _open: {
        opacity: 1,
        transformOrigin: 'var(--transform-origin)',
        animationDuration: 'fast',
        '&[data-placement^=top]': { animationName: 'slide-from-bottom' },
        '&[data-placement^=bottom]': { animationName: 'slide-from-top' },
        '&[data-placement^=left]': { animationName: 'slide-from-right' },
        '&[data-placement^=right]': { animationName: 'slide-from-left' },
        _motionReduce: {
          animation: 'none !important',
        },
      },
      _closed: {
        opacity: 0,
        _motionReduce: {
          animation: 'none !important',
        },
        _motionSafe: {
          animationStyle: 'slide-fade-out',
          animationDuration: 'faster',
        },
      },
    },
    item: {
      px: Space.SM,
      py: Space.XS,
      bg: Colors.TRANSPARENT,
      borderRadius: Radii.LG,
      color: Colors.TEXT_EMPHASIS,
      gap: Space.XS,
      textStyle: TextStyles.UI_DEFAULT,
      fontWeight: FontWeights.REGULAR,
      '& .chakra-menu__icon-wrapper': {
        fontSize: '1em',
        me: 0,
        color: Colors.ICON_DEFAULT,
      },
      _hover: {
        '&:not([disabled])': {
          bg: Colors.SURFACE_ELEVATED_HOVER,
          color: Colors.TEXT_EMPHASIS,
        },
      },
      _focus: {
        bg: Colors.SURFACE_ELEVATED_HOVER,
        color: Colors.TEXT_EMPHASIS,
      },
      _focusVisible: {
        boxShadow: Shadows.OUTLINE,
      },
      _active: {
        bg: Colors.SURFACE_ELEVATED_ACTIVE,
        color: Colors.TEXT_EMPHASIS,
      },
      '& small': {
        color: Colors.TEXT_MUTED,
        textStyle: TextStyles.P3,
      },
    },
    triggerItem: {
      px: Space.SM,
      py: Space.XS,
      bg: Colors.TRANSPARENT,
      borderRadius: Radii.LG,
      color: Colors.TEXT_EMPHASIS,
      gap: Space.XS,
      textStyle: TextStyles.UI_DEFAULT,
      fontWeight: FontWeights.REGULAR,
      display: 'flex',
      alignItems: 'center',
      cursor: 'menuitem',
      _hover: {
        '&:not([disabled])': {
          bg: Colors.SURFACE_ELEVATED_HOVER,
          color: Colors.TEXT_EMPHASIS,
        },
      },
      _focus: {
        bg: Colors.SURFACE_ELEVATED_HOVER,
        color: Colors.TEXT_EMPHASIS,
      },
      _focusVisible: {
        boxShadow: Shadows.OUTLINE,
      },
    },
    itemGroupLabel: {
      mb: Space['2XS'],
      mx: Space.SM,
      gap: Space.XS,
      color: Colors.TEXT_SUBDUED,
      textStyle: TextStyles.P3,
    },
    separator: {
      borderColor: Colors.BORDER_DEFAULT,
      mx: -1,
      my: Space.XS,
    },
  },
  variants: {
    size: {
      // Stock's md size outranks base, so the 14rem (3xs) list floor and our list/item insets are restated here.
      md: {
        content: { minW: '3xs', p: Space['2XS'] },
        item: { px: Space.SM, py: Space.XS },
      },
    },
  },
});
